import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import axios from "../../util/axiosInstance";
import {
  Download,
  Copy,
  FileSpreadsheet,
  FileText,
  FileType,
} from "lucide-react";
import { ChevronDown } from "lucide-react";
type Unit = { id: string; name: string };
type DayOption = {
  year: number;
  month: number;
  day: number;
  label: string;
  value: string;
};

interface DailyReportsProps {
  units: Unit[];
  loadingUnitSearchData: boolean;
}

const getLast8Days = (): DayOption[] => {
  const days: DayOption[] = [];
  const today = new Date();

  for (let i = 1; i <= 8; i++) {
    const past = new Date(today);
    past.setDate(today.getDate() - i);

    const year = past.getFullYear();
    const month = past.getMonth() + 1;
    const day = past.getDate();

    days.push({
      year,
      month,
      day,
      label: past.toLocaleDateString("en-US", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
      }),
      value: `${year}-${month}-${day}`,
    });
  }

  return days;
};

export default function DailyReports({
  units,
  loadingUnitSearchData,
}: DailyReportsProps) {
  const dayOptions = getLast8Days();
  const [selectedDay, setSelectedDay] = useState(dayOptions[0].value);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportData, setReportData] = useState<any>(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState("Unit Name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedUnit, setExpandedUnit] = useState<string | null>(null);
  const unitMap: Record<string, string> =
    units?.reduce((acc, u) => {
      acc[u.id] = u.name;
      return acc;
    }, {} as Record<string, string>) || {};

  const filteredReportData = reportData
    ? Object.entries(reportData).filter(([unitId]) =>
        (unitMap[unitId] || unitId)
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      )
    : [];

  const fetchDailyReport = async (dayValue: string) => {
    const dayObj = dayOptions.find((d) => d.value === dayValue);
    if (!dayObj || !units || units.length === 0) return;

    try {
      setLoadingReport(true);
      setError("");
      const { year, month, day } = dayObj;
      const payload = {
        units: units.map((u) => u.id),
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/reports/precision-report-day/${year}/${month}/${day}`,
        payload,
        {
          headers: { id: "anything", "Content-Type": "application/json" },
        }
      );

      setReportData(response.data.data.response);
    } catch (err) {
      console.error(err);
      setError("Error fetching report data");
    } finally {
      setLoadingReport(false);
    }
  };

  useEffect(() => {
    if (units && units.length > 0 && selectedDay) {
      fetchDailyReport(selectedDay);
    }
  }, [selectedDay, units]);

  useEffect(() => setPage(0), [searchQuery]);

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const copyToClipboard = () => {
    if (!filteredReportData.length) return;
    const headers =
      "Unit Name\tYear\tMonth\tDay\tStop(Hrs)\tRun(Hrs)\tCustomer(Hrs)\tPRC(Hrs)\tShutdowns";
    const textContent = filteredReportData
      .map(
        ([unitId, r]: [string, any]) =>
          `${unitMap[unitId] || unitId}\t${r.year}\t${r.month}\t${
            r.day
          }\t${r.down.toFixed(1)}\t${r.up.toFixed(1)}\t${r.customer.toFixed(
            1
          )}\t${r.company.toFixed(1)}\t${r.shutDowns}`
      )
      .join("\n");
    navigator.clipboard
      .writeText(`${headers}\n${textContent}`)
      .then(() => toast.success("Daily report copied to clipboard"));
  };

  const exportToCSV = () => {
    if (!filteredReportData.length) return;
    const headers = [
      "Unit Name",
      "Year",
      "Month",
      "Day",
      "Stop (Hrs)",
      "Run (Hrs)",
      "Customer (Hrs)",
      "PRC (Hrs)",
      "Shutdowns",
    ];
    const rows = filteredReportData.map(([unitId, r]: [string, any]) => [
      unitMap[unitId] || unitId,
      r.year,
      r.month,
      r.day,
      r.down.toFixed(1),
      r.up.toFixed(1),
      r.customer.toFixed(1),
      r.company.toFixed(1),
      r.shutDowns,
    ]);
    const csvString = [headers, ...rows]
      .map((r) => r.map((f) => `"${f}"`).join(","))
      .join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Daily_Report.csv`;
    link.click();
    toast.success("Exported daily Report to CSV");
  };

  const exportToExcel = () => {
    if (!filteredReportData.length) return;
    const headers = [
      "Unit Name",
      "Year",
      "Month",
      "Day",
      "Stop (Hrs)",
      "Run (Hrs)",
      "Customer (Hrs)",
      "PRC (Hrs)",
      "Shutdowns",
    ];
    const rows = filteredReportData.map(([unitId, r]: [string, any]) => [
      unitMap[unitId] || unitId,
      r.year,
      r.month,
      r.day,
      r.down.toFixed(1),
      r.up.toFixed(1),
      r.customer.toFixed(1),
      r.company.toFixed(1),
      r.shutDowns,
    ]);
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Daily Report");
    XLSX.writeFile(workbook, `Daily_Report.xlsx`);
    toast.success("Exported daily Report to Excel");
  };

  const exportToPDF = () => {
    if (!filteredReportData.length) return;
    const doc = new jsPDF("l", "pt", "a4");
    doc.setFontSize(12);
    doc.text("Daily Units Report", 40, 30);
    const headers = [
      [
        "Unit Name",
        "Year",
        "Month",
        "Day",
        "Stop (Hrs)",
        "Run (Hrs)",
        "Customer (Hrs)",
        "PRC (Hrs)",
        "Shutdowns",
      ],
    ];
    const rows = filteredReportData.map(([unitId, r]: [string, any]) => [
      unitMap[unitId] || unitId,
      r.year,
      new Date(r.year, r.month - 1).toLocaleString("default", {
        month: "long",
      }),
      r.day,
      r.down.toFixed(1),
      r.up.toFixed(1),
      r.customer.toFixed(1),
      r.company.toFixed(1),
      r.shutDowns,
    ]);
    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 50,
      theme: "grid",
      styles: { fontSize: 8, cellPadding: 3 },
    });
    doc.save("Daily_Report.pdf");
    toast.success("Exported daily Report to PDF");
  };

  const sortedData = React.useMemo(() => {
    if (!filteredReportData) return [];
    return [...filteredReportData].sort(
      ([aId, aR]: [string, any], [bId, bR]: [string, any]) => {
        let aValue: any, bValue: any;
        switch (orderBy) {
          case "Unit Name":
            aValue = unitMap[aId] || aId;
            bValue = unitMap[bId] || bId;
            break;
          case "Year":
            aValue = aR.year;
            bValue = bR.year;
            break;
          case "Month":
            aValue = aR.month;
            bValue = bR.month;
            break;
          case "Day":
            aValue = aR.day;
            bValue = bR.day;
            break;
          case "Stop(Hrs)":
            aValue = aR.down;
            bValue = bR.down;
            break;
          case "Run(Hrs)":
            aValue = aR.up;
            bValue = bR.up;
            break;
          case "Customer(Hrs)":
            aValue = aR.customer;
            bValue = bR.customer;
            break;
          case "PRC(Hrs)":
            aValue = aR.company;
            bValue = bR.company;
            break;
          case "Shutdowns":
            aValue = aR.shutDowns;
            bValue = bR.shutDowns;
            break;
          default:
            return 0;
        }
        if (typeof aValue === "string") {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }
        if (aValue < bValue) return order === "asc" ? -1 : 1;
        if (aValue > bValue) return order === "asc" ? 1 : -1;
        return 0;
      }
    );
  }, [filteredReportData, order, orderBy]);

  const paginatedUnits = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <div className="w-full flex flex-col rounded-lg bg-slate-900  p-5">
      {loadingUnitSearchData && (
        <div className="absolute inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-20">
          <div className="animate-spin h-12 w-12 border-4 border-blue-400 border-t-transparent rounded-full" />
        </div>
      )}

      <div className="flex justify-between items-center flex-wrap gap-2 px-4 mt-2 mb-4">
        <h1 className="text-xl font-bold mt-5">Daily Runtime Report</h1>
      </div>

      {loadingReport ? (
        <div className="w-full flex justify-center items-center min-h-[200px]">
          <div className="animate-spin h-12 w-12 border-4 border-blue-400 border-t-transparent rounded-full" />
        </div>
      ) : error ? (
        <div className="text-red-500 font-bold text-center py-4">{error}</div>
      ) : (
        <>
          <div className="p-3 bg-slate-900 flex flex-col gap-3 md:flex-row md:items-center md:gap-2">
            <input
              type="text"
              placeholder="Search Unit..."
              className="px-2 py-1 rounded bg-slate-800 text-white border border-slate-600 flex-1"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="grid grid-cols-4 gap-2 md:flex md:gap-2 md:w-auto">
              <button
                onClick={copyToClipboard}
                className="flex items-center justify-center gap-1 px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 text-sm whitespace-nowrap"
              >
                <Copy size={12} className="inline-flex align-middle" /> Copy
              </button>
              <button
                onClick={exportToCSV}
                className="flex items-center justify-center gap-1 px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 text-sm whitespace-nowrap"
              >
                <FileText size={12} className="inline-flex align-middle" /> CSV
              </button>
              <button
                onClick={exportToExcel}
                className="flex items-center justify-center gap-1 px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 text-sm whitespace-nowrap"
              >
                <FileSpreadsheet
                  size={12}
                  className="inline-flex align-middle"
                />{" "}
                Excel
              </button>
              <button
                onClick={exportToPDF}
                className="flex items-center justify-center gap-1 px-3 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 text-sm whitespace-nowrap"
              >
                <FileType size={12} className="inline-flex align-middle" /> PDF
              </button>
            </div>
            <select
              className="px-2 py-1 bg-gray-800 text-white rounded border border-gray-600"
              value={selectedDay}
              onChange={(e) => setSelectedDay(e.target.value)}
            >
              {dayOptions.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>

          <div className="overflow-auto max-h-[63vh] px-2 mt-5 rounded-lg">
            <div className="rounded-xl overflow-hidden border border-gray-700">
              <table className="w-full text-left border-collapse">
                <thead className="bg-gray-700 text-white sticky top-0 hidden sm:table-header-group">
                  <tr className="border-b border-slate-700 bg-slate-800">
                    {[
                      "Unit Name",
                      "Year",
                      "Month",
                      "Day",
                      "Stop(Hrs)",
                      "Run(Hrs)",
                      "Customer(Hrs)",
                      "PRC(Hrs)",
                      "Shutdowns",
                    ].map((col) => (
                      <th
                        key={col}
                        className="p-2 cursor-pointer select-none font-semibold text-center"
                        onClick={() => handleSort(col)}
                      >
                        <div className="flex items-center justify-center w-full">
                          <span className="flex items-center gap-1">{col}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedUnits.length ? (
                    paginatedUnits.map(([unitId, r]: [string, any], idx) => (
                      <React.Fragment key={unitId}>
                        <tr
                          className={`hidden sm:table-row ${
                            idx % 2 === 0 ? "bg-gray-800" : "bg-gray-700"
                          }`}
                        >
                          <td className="px-2 py-1">
                            {unitMap[unitId] || unitId}
                          </td>
                          <td className="px-2 py-1 text-center">{r.year}</td>
                          <td className="px-2 py-1 text-center">{r.month}</td>
                          <td className="px-2 py-1 text-center">{r.day}</td>
                          <td className="px-2 py-1 text-center">
                            {r.down.toFixed(1)}
                          </td>
                          <td className="px-2 py-1 text-center">
                            {r.up.toFixed(1)}
                          </td>
                          <td className="px-2 py-1 text-center">
                            {r.customer.toFixed(1)}
                          </td>
                          <td className="px-2 py-1 text-center">
                            {r.company.toFixed(1)}
                          </td>
                          <td className="px-2 py-1 text-center">
                            {r.shutDowns}
                          </td>
                        </tr>
                        <tr className="sm:hidden bg-gray-800 border-b border-gray-700">
                          <td colSpan={9} className="p-3">
                            {/* Header */}
                            <button
                              onClick={() =>
                                setExpandedUnit(
                                  expandedUnit === unitId ? null : unitId
                                )
                              }
                              className="w-full flex justify-between items-center text-left"
                            >
                              <span className="font-semibold text-[14px] text-white">
                                {unitMap[unitId] || unitId}
                              </span>
                              <ChevronDown
                                className={`w-4 h-4 transition-transform ${
                                  expandedUnit === unitId ? "rotate-180" : ""
                                }`}
                              />
                            </button>

                            {/* Expanded content */}
                            {expandedUnit === unitId && (
                              <div className="mt-3 space-y-2 text-sm text-gray-300">
                                <div className="flex justify-between">
                                  <span>Year</span>
                                  <span>{r.year}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Month</span>
                                  <span>{r.month}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Day</span>
                                  <span>{r.day}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Stop (Hrs)</span>
                                  <span>{r.down.toFixed(1)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Run (Hrs)</span>
                                  <span>{r.up.toFixed(1)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Customer (Hrs)</span>
                                  <span>{r.customer.toFixed(1)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>PRC (Hrs)</span>
                                  <span>{r.company.toFixed(1)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span>Shutdowns</span>
                                  <span>{r.shutDowns}</span>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      </React.Fragment>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="text-center py-4">
                        No units found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex justify-end items-center py-2 px-3 text-white gap-2">
            <div className="w-32 text-right">Rows:</div>
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(0);
              }}
              className="bg-gray-800 border border-gray-600 rounded px-1 py-1"
            >
              {[5, 10, 25, 50, 100].map((n) => (
                <option key={n}>{n}</option>
              ))}
            </select>
          </div>
        </>
      )}
    </div>
  );
}
